import type { DetailTab, RadarItem } from './property-shared';
import { getRadarItems, isAgreementActive, renderPropertySummary, type DetailTab as DetailTabType } from './property-shared';
import type { DamageReportDto, DocumentDto, InsuranceDto, MaintenanceActivityDto, PropertyDto, ReminderDto, RentalAgreementDto } from '../../services/api';
import { t } from '../../services/i18n';
import './rc-property-tenants-tab';
import './rc-property-rent-history-tab';
import './rc-property-occupancy-tab';
import './rc-property-contract-renewals-tab';
import './rc-property-tenant-turnovers-tab';
import './rc-property-documents-tab';
import './rc-property-gallery-tab';
import './rc-property-reminders-tab';
import './rc-property-maintenance-tab';
import './rc-property-radar-tab';
import './rc-property-public-page-tab';
import './rc-property-damage-reports-tab';
import '../insurance/rc-property-insurances-tab';

export class RcPropertyDetailsView extends HTMLElement {
  property: PropertyDto | null = null;
  agreements: RentalAgreementDto[] = [];
  documents: DocumentDto[] = [];
  reminders: ReminderDto[] = [];
  maintenanceActivities: MaintenanceActivityDto[] = [];
  insurances: InsuranceDto[] = [];
  damageReports: DamageReportDto[] = [];
  activeTab: DetailTabType = 'tenants';
  renewalWorkflowCount = 0;
  turnoverChecklistCount = 0;

  connectedCallback(): void {
    this.addEventListener('click', this.handleClick);
    this.addEventListener('property-tab-change', this.handleTabChange as EventListener);
    this.addEventListener('property-renewal-count-changed', this.handleRenewalCountChanged as EventListener);
    this.addEventListener('property-turnover-count-changed', this.handleTurnoverCountChanged as EventListener);
    this.render();
  }

  disconnectedCallback(): void {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('property-tab-change', this.handleTabChange as EventListener);
    this.removeEventListener('property-renewal-count-changed', this.handleRenewalCountChanged as EventListener);
    this.removeEventListener('property-turnover-count-changed', this.handleTurnoverCountChanged as EventListener);
  }

  render(): void {
    if (!this.property) {
      this.innerHTML = '';
      return;
    }

    const radarItems = getRadarItems(this.documents, this.agreements, this.reminders);
    const galleryPhotos = this.property.galleryPhotos || [];
    const overviewStats = [
      {
        label: t('nav.tenants'),
        value: String(this.agreements.filter((agreement) => isAgreementActive(agreement)).length),
        tab: 'tenants',
      },
      {
        label: t('property.renewals.title'),
        value: String(this.renewalWorkflowCount),
        tab: 'renewals',
      },
      {
        label: t('property.turnovers.title'),
        value: String(this.turnoverChecklistCount),
        tab: 'turnovers',
      },
      {
        label: t('nav.documents'),
        value: String(this.documents.length),
        tab: 'documents',
      },
      {
        label: t('nav.reminders'),
        value: String(this.reminders.filter((reminder) => !reminder.isCompleted).length),
        tab: 'reminders',
      },
      {
        label: t('maintenanceActivities.tabTitle'),
        value: String(this.maintenanceActivities.length),
        tab: 'maintenance',
      },
      {
        label: t('nav.damageReports'),
        value: String(this.damageReports.length),
        tab: 'damage-reports',
      },
      {
        label: t('property.radar.title'),
        value: String(radarItems.length),
        tab: 'radar',
      },
    ] as const;
    this.innerHTML = `
      <div class="detail-panel">
        <div class="property-detail-overview">
          <div class="detail-panel-info">${renderPropertySummary(this.property)}</div>
          <div class="property-overview-stats" aria-label="${t('common.details')}">
            ${overviewStats.map((stat) => `
              <button class="property-overview-stat ${this.activeTab === stat.tab ? 'active' : ''}" data-tab="${stat.tab}">
                <span class="property-overview-stat-label">${stat.label}</span>
                <span class="property-overview-stat-value">${stat.value}</span>
              </button>
            `).join('')}
          </div>
        </div>
        <div class="property-detail-workspace">
          <div class="detail-tabs" role="tablist" aria-label="${t('common.details')}">
            <button class="detail-tab ${this.activeTab === 'tenants' ? 'active' : ''}" data-tab="tenants">
              <span class="detail-tab-label">${t('nav.tenants')}</span>
              <span class="tab-count">${this.agreements.length}</span>
            </button>
            <button class="detail-tab ${this.activeTab === 'rent-history' ? 'active' : ''}" data-tab="rent-history">
              <span class="detail-tab-label">${t('property.rentHistory.title')}</span>
              <span class="tab-count">${this.agreements.filter((agreement) => Boolean(agreement.startDate)).length}</span>
            </button>
            <button class="detail-tab ${this.activeTab === 'occupancy' ? 'active' : ''}" data-tab="occupancy">
              <span class="detail-tab-label">${t('property.occupancy.title')}</span>
              <span class="tab-count">${this.agreements.filter((agreement) => Boolean(agreement.startDate)).length}</span>
            </button>
            <button class="detail-tab ${this.activeTab === 'renewals' ? 'active' : ''}" data-tab="renewals">
              <span class="detail-tab-label">${t('property.renewals.title')}</span>
              <span class="tab-count">${this.renewalWorkflowCount}</span>
            </button>
            <button class="detail-tab ${this.activeTab === 'turnovers' ? 'active' : ''}" data-tab="turnovers">
              <span class="detail-tab-label">${t('property.turnovers.title')}</span>
              <span class="tab-count">${this.turnoverChecklistCount}</span>
            </button>
            <button class="detail-tab ${this.activeTab === 'documents' ? 'active' : ''}" data-tab="documents">
              <span class="detail-tab-label">${t('nav.documents')}</span>
              <span class="tab-count">${this.documents.length}</span>
            </button>
            <button class="detail-tab ${this.activeTab === 'gallery' ? 'active' : ''}" data-tab="gallery">
              <span class="detail-tab-label">${t('property.gallery.title')}</span>
              <span class="tab-count">${galleryPhotos.length}</span>
            </button>
            <button class="detail-tab ${this.activeTab === 'reminders' ? 'active' : ''}" data-tab="reminders">
              <span class="detail-tab-label">${t('nav.reminders')}</span>
              <span class="tab-count">${this.reminders.length}</span>
            </button>
            <button class="detail-tab ${this.activeTab === 'maintenance' ? 'active' : ''}" data-tab="maintenance">
              <span class="detail-tab-label">${t('maintenanceActivities.tabTitle')}</span>
              <span class="tab-count">${this.maintenanceActivities.length}</span>
            </button>
            <button class="detail-tab ${this.activeTab === 'radar' ? 'active' : ''}" data-tab="radar">
              <span class="detail-tab-label">${t('property.radar.title')}</span>
              <span class="tab-count">${radarItems.length}</span>
            </button>
            <button class="detail-tab ${this.activeTab === 'insurances' ? 'active' : ''}" data-tab="insurances">
              <span class="detail-tab-label">${t('insurance.tabTitle')}</span>
              <span class="tab-count">${this.insurances.length}</span>
            </button>
            <button class="detail-tab ${this.activeTab === 'damage-reports' ? 'active' : ''}" data-tab="damage-reports">
              <span class="detail-tab-label">${t('nav.damageReports')}</span>
              <span class="tab-count">${this.damageReports.length}</span>
            </button>
            <button class="detail-tab ${this.activeTab === 'public-page' ? 'active' : ''}" data-tab="public-page">
              <span class="detail-tab-label">${t('property.publicPage.tabTitle')}</span>
            </button>
          </div>
          <div class="detail-tab-content">
            <rc-property-tenants-tab ${this.activeTab === 'tenants' ? '' : 'hidden'}></rc-property-tenants-tab>
            <rc-property-rent-history-tab ${this.activeTab === 'rent-history' ? '' : 'hidden'}></rc-property-rent-history-tab>
            <rc-property-occupancy-tab ${this.activeTab === 'occupancy' ? '' : 'hidden'}></rc-property-occupancy-tab>
            <rc-property-contract-renewals-tab ${this.activeTab === 'renewals' ? '' : 'hidden'}></rc-property-contract-renewals-tab>
            <rc-property-tenant-turnovers-tab ${this.activeTab === 'turnovers' ? '' : 'hidden'}></rc-property-tenant-turnovers-tab>
            <rc-property-documents-tab ${this.activeTab === 'documents' ? '' : 'hidden'}></rc-property-documents-tab>
            <rc-property-gallery-tab ${this.activeTab === 'gallery' ? '' : 'hidden'}></rc-property-gallery-tab>
            <rc-property-reminders-tab ${this.activeTab === 'reminders' ? '' : 'hidden'}></rc-property-reminders-tab>
            <rc-property-maintenance-tab ${this.activeTab === 'maintenance' ? '' : 'hidden'}></rc-property-maintenance-tab>
            <rc-property-radar-tab ${this.activeTab === 'radar' ? '' : 'hidden'}></rc-property-radar-tab>
            <rc-property-insurances-tab ${this.activeTab === 'insurances' ? '' : 'hidden'}></rc-property-insurances-tab>
            <rc-property-damage-reports-tab ${this.activeTab === 'damage-reports' ? '' : 'hidden'}></rc-property-damage-reports-tab>
            <rc-property-public-page-tab ${this.activeTab === 'public-page' ? '' : 'hidden'}></rc-property-public-page-tab>
          </div>
        </div>
      </div>
    `;

    const tenantsTab = this.querySelector<any>('rc-property-tenants-tab');
    if (tenantsTab) {
      tenantsTab.agreements = this.agreements;
      tenantsTab.render();
    }
    const rentHistoryTab = this.querySelector<any>('rc-property-rent-history-tab');
    if (rentHistoryTab) {
      rentHistoryTab.agreements = this.agreements;
      rentHistoryTab.render();
    }
    const occupancyTab = this.querySelector<any>('rc-property-occupancy-tab');
    if (occupancyTab) {
      occupancyTab.agreements = this.agreements;
      occupancyTab.render();
    }
    const renewalsTab = this.querySelector<any>('rc-property-contract-renewals-tab');
    if (renewalsTab) {
      renewalsTab.propertyId = this.property.id;
      renewalsTab.propertyAddress = this.property.displayName || this.property.address;
      renewalsTab.agreements = this.agreements;
      renewalsTab.render();
    }
    const turnoversTab = this.querySelector<any>('rc-property-tenant-turnovers-tab');
    if (turnoversTab) {
      turnoversTab.propertyId = this.property.id;
      turnoversTab.propertyAddress = this.property.displayName || this.property.address;
      turnoversTab.agreements = this.agreements;
      turnoversTab.render();
    }
    const documentsTab = this.querySelector<any>('rc-property-documents-tab');
    if (documentsTab) {
      documentsTab.documents = this.documents;
      documentsTab.render();
    }
    const galleryTab = this.querySelector<any>('rc-property-gallery-tab');
    if (galleryTab) {
      galleryTab.propertyId = this.property.id;
      galleryTab.photos = galleryPhotos;
      galleryTab.render();
    }
    const remindersTab = this.querySelector<any>('rc-property-reminders-tab');
    if (remindersTab) {
      remindersTab.reminders = this.reminders;
      remindersTab.render();
    }
    const maintenanceTab = this.querySelector<any>('rc-property-maintenance-tab');
    if (maintenanceTab) {
      maintenanceTab.activities = this.maintenanceActivities;
      maintenanceTab.render();
    }
    const radarTab = this.querySelector<any>('rc-property-radar-tab');
    if (radarTab) {
      radarTab.items = radarItems;
      radarTab.render();
    }
    const insurancesTab = this.querySelector<any>('rc-property-insurances-tab');
    if (insurancesTab) {
      insurancesTab.insurances = this.insurances;
      insurancesTab.render();
    }
    const damageReportsTab = this.querySelector<any>('rc-property-damage-reports-tab');
    if (damageReportsTab) {
      damageReportsTab.reports = this.damageReports;
      damageReportsTab.render();
    }
    const publicPageTab = this.querySelector<any>('rc-property-public-page-tab');
    if (publicPageTab) {
      publicPageTab.propertyId = this.property.id;
      publicPageTab.property = this.property;
      publicPageTab.render();
    }
  }

  private updateActiveTabUi(): void {
    const tabs = this.querySelectorAll<HTMLElement>('.detail-tab[data-tab]');
    tabs.forEach((tab) => {
      tab.classList.toggle('active', tab.dataset.tab === this.activeTab);
    });
    const overviewStats = this.querySelectorAll<HTMLElement>('.property-overview-stat[data-tab]');
    overviewStats.forEach((stat) => {
      stat.classList.toggle('active', stat.dataset.tab === this.activeTab);
    });

    const tabPanels: Array<[DetailTabType, string]> = [
      ['tenants', 'rc-property-tenants-tab'],
      ['rent-history', 'rc-property-rent-history-tab'],
      ['occupancy', 'rc-property-occupancy-tab'],
      ['renewals', 'rc-property-contract-renewals-tab'],
      ['turnovers', 'rc-property-tenant-turnovers-tab'],
      ['documents', 'rc-property-documents-tab'],
      ['gallery', 'rc-property-gallery-tab'],
      ['reminders', 'rc-property-reminders-tab'],
      ['maintenance', 'rc-property-maintenance-tab'],
      ['radar', 'rc-property-radar-tab'],
      ['insurances', 'rc-property-insurances-tab'],
      ['damage-reports', 'rc-property-damage-reports-tab'],
      ['public-page', 'rc-property-public-page-tab'],
    ];

    tabPanels.forEach(([tabName, selector]) => {
      const panel = this.querySelector<HTMLElement>(selector);
      if (!panel) return;
      panel.toggleAttribute('hidden', tabName !== this.activeTab);
    });
  }

  private handleClick = (event: Event): void => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>('.detail-tab,.property-overview-stat,[data-action]');
    if (!target) return;
    if ((target.classList.contains('detail-tab') || target.classList.contains('property-overview-stat')) && target.dataset.tab) {
      this.dispatchEvent(new CustomEvent('property-tab-change', { bubbles: true, detail: { tab: target.dataset.tab } }));
      return;
    }
    if (target.dataset.action) {
      this.dispatchEvent(new CustomEvent(`property-details-${target.dataset.action}`, {
        bubbles: true,
        detail: { id: target.dataset.id, tab: target.dataset.tab },
      }));
    }
  };

  private handleTabChange = (event: CustomEvent<{ tab: DetailTab }>): void => {
    if (!event.detail?.tab) return;
    this.activeTab = event.detail.tab;
    this.updateActiveTabUi();
  };

  private handleRenewalCountChanged = (event: CustomEvent<{ count: number }>): void => {
    if (typeof event.detail?.count !== 'number' || event.detail.count === this.renewalWorkflowCount) {
      return;
    }

    this.renewalWorkflowCount = event.detail.count;
    this.render();
  };

  private handleTurnoverCountChanged = (event: CustomEvent<{ count: number }>): void => {
    if (typeof event.detail?.count !== 'number' || event.detail.count === this.turnoverChecklistCount) {
      return;
    }

    this.turnoverChecklistCount = event.detail.count;
    this.render();
  };
}

if (!customElements.get('rc-property-details-view')) {
  customElements.define('rc-property-details-view', RcPropertyDetailsView);
}
